import {BUBBLE, Component, DONE, isInside, TargetPosition, TOUCH} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {applyRange, combineRange, Coordinates, decodeNumericIconEntity, Range, Rect} from "@/event/calendar/Shapes.ts";
import type {Animation, AnimationID} from "@/event/calendar/dom/Animation.ts";
import {measureRuns, TextRun} from "@/canvas/CanvasUtils.ts";
import {Icon} from "anneca-symbols";
import {ICON_FONT} from "@/event/calendar/Constants.ts";

const SNAP_WAIT = 100;
const SNAP_DURATION = 200;

const DRAG_AUTOSCROLL = TOUCH ? 64 : 40;
const DRAG_SCROLL_WAIT_MS = 600;
const AUTOSCROLL_INTERVAL = TOUCH ? 100 : 50;
/* Maximum speed in px/s of auto scroll */
const MAX_SPEED = 600;
const MIN_SPEED = 50;
/* Number of ms in autoscroll zone required to reach max speed */
const TIME_TO_MAX_SPEED = 1000;
type AutoscrollDir = "top" | "bottom" | "right" | "left";

const mac = typeof navigator != "undefined" ? /Mac|iP(hone|[oa]d)/.test(navigator.platform)
	// @ts-ignore
	: typeof os != "undefined" && os.platform ? os.platform() == "darwin" : false;

const TIP_HEIGHT = 26;
const TIP_OFFSET = 8;
const TIP_SPACING = 4;
const TIP_BASELINE = 18;
const TIP_PADDING = 16;
const TIP_AGE = 10000;

const IDLE_TIMEOUT = 1000;

export const SCROLLBAR_WIDTH = TOUCH ? 26 : 14;
export const SCROLLBAR_DISTANCE =  TOUCH ? 4 : 2;


/** If the distance to snap position is less than this value, snapping algorithm will ignore scrolling direction */
export const MIN_SNAP_DISTANCE = 32;


export class ScrollComponent extends Component {

	private _scrollX:number;
	private _scrollY:number;

	private horizontalSyncs:ScrollComponent[];
	private verticalSyncs:ScrollComponent[];

	private allowHorizontal: boolean;
	private allowVertical: boolean;
	private allowTip: boolean;
	private verticalScrollbar: boolean;
	private horizontalScrollbar: boolean;
	private verticalScrollbarShape: Path2D|null;
	private horizontalScrollbarShape: Path2D|null;
	/** Events are not delivered to children (used for overlay UI elements like scrollbars) */
	private blockChildren:boolean;
	/** Position of the scroll at the start of scrolling */
	private scrollStartX:number;
	private scrollStartY:number;
	/** Position of the first click on the scrollbar */
	private scrollStartPointerX:number;
	private scrollStartPointerY:number;
	/** State information: what scrollbar is being dragged */
	private scrollDrag:"horizontal"|"vertical"|"no";
	private scrollRatio:number;

	/** How many pixels from the pane edge starts autoscroll 0 = no autoscroll */
	public horizontalDragScroll:number;
	public verticalDragScroll:number;

	private snapScrollTimer:number;
	private snapAnimation:AnimationID = 0;
	private idleTimeout:number = 0;
	private firstLayout:boolean;

	/** Timestamp of the first entering of drag-scroll-zone (zone where scrolling is invoked) */
	private enteredDragScrollZone = 0;

	private created:number;
	private lastHorizontalScroll:number;
	private lastVerticalScroll:number;

	private horizontalPositiveDirection:boolean;
	private verticalPositiveDirection:boolean;


	constructor(component:Component, allowHorizontal = true, allowVertical = true, allowTip = false) {
		super();

		this.created = Date.now();
		this.lastHorizontalScroll = -1;
		this.lastVerticalScroll = -1;

		this._scrollX = 0;
		this._scrollY = 0;
		this.firstLayout = true;	// to enable initialization of scroll
		component.clip = false;
		this.allowHorizontal = allowHorizontal;
		this.allowVertical = allowVertical;
		this.append(component);
		this.horizontalDragScroll = DRAG_AUTOSCROLL;
		this.verticalDragScroll = DRAG_AUTOSCROLL;
		this.allowTip = allowTip;
		this.horizontalPositiveDirection = this.verticalPositiveDirection = true;
		this.blockChildren = false;
		this.scrollDrag = "no";
	}


	withScrollbars(allowVertical: boolean, allowHorizontal: boolean) {
		this.verticalScrollbar = allowVertical;
		this.horizontalScrollbar = allowHorizontal;
		return this;
	}


	get scrollX(): number {
		return this._scrollX;
	}

	set scrollX(value: number) {
		if (this.allowHorizontal) {
			const lastValue = this._scrollX;
			value = applyRange(this.calcHorizontalScrollRange(), value);
			this._scrollX = value;
			if (Array.isArray(this.horizontalSyncs)) {
				for (let other of this.horizontalSyncs) other._scrollX = this._scrollX;
			}
			if (lastValue !== this._scrollX) {
				this.lastHorizontalScroll = Date.now();
				this.scrollChanged();
			}
		}
	}

	get scrollY(): number {
		return this._scrollY;
	}

	set scrollY(value: number) {
		if (this.allowVertical) {
			const lastValue = this._scrollY;
			value = applyRange(this.calcVerticalScrollRange(), value);
			this._scrollY = value;
			if (Array.isArray(this.verticalSyncs)) {
				for (let other of this.verticalSyncs) other._scrollY = this._scrollY;
			}
			if (lastValue !== this._scrollY) {
				this.lastVerticalScroll = Date.now();
				this.scrollChanged();
			}
		}
	}

	scrollChanged() {
		if (this.idleTimeout) {
			clearTimeout(this.idleTimeout);
			this.idleTimeout = 0;
		}
		this.idleTimeout = window.setTimeout(() => {
			// Notify children that scrolling ended and is calm now
			this.idleTimeout = 0;
			if (this.children) {
				for (let child of this.children) {
					child.scrollIdle();
				}
			}
		}, IDLE_TIMEOUT);
	}

	layoutSelf(dc: DrawingContext, contentBox: Rect) {
		// Setting initial scroll as requested by a child
		if (this.firstLayout) {
			this.firstLayout = false;
			if (this.children) {
				for (let ch of this.children) {
					const scroll = ch.initialScroll();
					if (scroll) {
						if (typeof scroll.x === 'number') this.scrollX = scroll.x;
						if (typeof scroll.y === 'number') this.scrollY = scroll.y;
						break;
					}
				}
			}
		}
	}

	private calcHorizontalScrollRange():Range {
		let range:Range = {};
		if (this.children) {
			for (let ch of this.children) {
				range = combineRange(range, ch.horizontalScrollRange());
			}
		}
		return range;
	}

	private calcVerticalScrollRange():Range {
		let range:Range = {};
		if (this.children) {
			for (let ch of this.children) {
				range = combineRange(range, ch.verticalScrollRange());
			}
		}
		return range;
	}

	private syncBack(other: ScrollComponent, horizontal: boolean = true) {
		if (horizontal) {
			if (!Array.isArray(this.horizontalSyncs)) this.horizontalSyncs = [];
			this.horizontalSyncs.push(other);
		} else {
			if (!Array.isArray(this.verticalSyncs)) this.verticalSyncs = [];
			this.verticalSyncs.push(other);
		}
	}

	syncWith(other: ScrollComponent, horizontal: boolean = true) {
		if (horizontal) {
			if (!Array.isArray(this.horizontalSyncs)) this.horizontalSyncs = [];
			this.horizontalSyncs.push(other);
		} else {
			if (!Array.isArray(this.verticalSyncs)) this.verticalSyncs = [];
			this.verticalSyncs.push(other);
		}
		other.syncBack(this, horizontal);
	}

	drawChildren(dc: DrawingContext, area: Rect) {
		if (this.children) {
			try {
				dc.ctx.save();
				//debugger;
				dc.ctx.translate(-1 * this._scrollX, -1 * this._scrollY);
				area = { x: area.x + this._scrollX, y: area.y + this._scrollY, width: area.width, height: area.height };
				dc.ctx.beginPath();
				dc.ctx.rect(area.x, area.y, area.width, area.height);
				dc.ctx.clip();

				for (let child of this.children) {
					child.draw(dc, area);
				}

			} finally {
				dc.ctx.restore();
			}
		}
		if (this.allowTip && this.allowHorizontal
			&& (this.created >= (Date.now() - TIP_AGE))
			&& this.lastHorizontalScroll < 0) this.drawScrollTip(dc);

		this.drawScrollBars(dc, area);
	}

	drawScrollBars(dc: DrawingContext, area: Rect) {
		if (!this.horizontalScrollbar && !this.verticalScrollbar) return;
		try {
			dc.ctx.save();
			dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-fg") + "33";

			let vs:boolean = false, hs:boolean = false;
			let scrollableHeight:number, scrollableWidth:number;
			if (this.verticalScrollbar) {
				const vRange = this.calcVerticalScrollRange();
				scrollableHeight = vRange.max - vRange.min + this.height;
				vs = (scrollableHeight > this.height);
			}
			if (this.horizontalScrollbar) {
				const hRange = this.calcHorizontalScrollRange();
				scrollableWidth = hRange.max - hRange.min + this.width;
				hs = (scrollableWidth > this.width);
			}
			const cornerSize = (vs && hs) ?  SCROLLBAR_WIDTH : 0;

			if (hs) {
				const drawWidth = this.width - 2 * SCROLLBAR_DISTANCE - cornerSize;
				const visibleRatio = this.width / scrollableWidth;
				const barLength = Math.round(drawWidth * visibleRatio);
				const scrollBarPixels = drawWidth - barLength;
				const scrollAreaPixels = scrollableWidth - this.width;
				this.scrollRatio = scrollBarPixels / scrollAreaPixels;
				const barOffset = Math.floor(this._scrollX * this.scrollRatio);
				dc.ctx.beginPath();
				dc.ctx.roundRect(SCROLLBAR_DISTANCE + barOffset, this.height - SCROLLBAR_WIDTH - SCROLLBAR_DISTANCE, barLength, SCROLLBAR_WIDTH, SCROLLBAR_WIDTH / 2);
				dc.ctx.fill();
				this.horizontalScrollbarShape = new Path2D();
				this.horizontalScrollbarShape.roundRect(SCROLLBAR_DISTANCE + barOffset, this.height - SCROLLBAR_WIDTH - SCROLLBAR_DISTANCE, barLength, SCROLLBAR_WIDTH, SCROLLBAR_WIDTH / 2);
			} else this.horizontalScrollbarShape = null;
			if (vs) {
				//debugger;
				const drawHeight = this.height - 2 * SCROLLBAR_DISTANCE - cornerSize;
				const visibleRatio = this.height / scrollableHeight;
				const barLength = Math.round(drawHeight * visibleRatio);
				const scrollBarPixels = drawHeight - barLength;
				const scrollAreaPixels = scrollableHeight - this.height;
				this.scrollRatio = scrollBarPixels / scrollAreaPixels;
				const barOffset = Math.floor(this._scrollY * this.scrollRatio);
				dc.ctx.beginPath();
				dc.ctx.roundRect(this.width - SCROLLBAR_WIDTH - SCROLLBAR_DISTANCE, SCROLLBAR_DISTANCE + barOffset, SCROLLBAR_WIDTH, barLength, SCROLLBAR_WIDTH / 2);
				dc.ctx.fill();
				this.verticalScrollbarShape = new Path2D();
				this.verticalScrollbarShape.roundRect(this.width - SCROLLBAR_WIDTH - SCROLLBAR_DISTANCE, SCROLLBAR_DISTANCE + barOffset, SCROLLBAR_WIDTH, barLength, SCROLLBAR_WIDTH / 2);
			} else this.verticalScrollbarShape = null;
		} finally {
			dc.ctx.restore();
		}
	}


	drawScrollTip(dc: DrawingContext) {
		try {
			dc.ctx.save();
			dc.ctx.fillStyle = "#00000080";
			dc.ctx.beginPath();

			const ctrl = decodeNumericIconEntity(Icon.parse("key-ctrl").entity);
			//const shift = decodeNumericIconEntity(Icon.parse("key-shift").entity);
			const mouse = decodeNumericIconEntity(Icon.parse("mouse").entity);
			const scrollHorizontal = decodeNumericIconEntity(Icon.parse("scroll-horizontal").entity);
			const plus = decodeNumericIconEntity(Icon.parse("plus-8").entity);
			const eq = decodeNumericIconEntity(Icon.parse("equal-8").entity);

			//const eventIcon = decodeNumericIconEntity(pi.entity);

			const runs:TextRun[] = [
				{ text: ctrl, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: plus, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				// { text: shift, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				// { text: plus, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: mouse, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: eq, font: "14px "+ICON_FONT, color: "#FFFFFF" },
				{ text: scrollHorizontal, font: "14px "+ICON_FONT, color: "#FFFFFF" }
			];
			const mruns = measureRuns(dc.ctx, runs);
			let totalWidth = (mruns.length - 1) * TIP_SPACING;
			mruns.forEach((mr) => { totalWidth += mr.x });
			dc.ctx.roundRect(this.width - TIP_OFFSET - totalWidth - 2 * TIP_PADDING, this.height - TIP_HEIGHT - TIP_OFFSET, totalWidth + 2 * TIP_PADDING, TIP_HEIGHT, 5);
			dc.ctx.fill();
			let start = this.width - TIP_OFFSET - totalWidth - TIP_PADDING;
			const baseline = this.height - TIP_HEIGHT - TIP_OFFSET + TIP_BASELINE;
			for (let mr of mruns) {
				dc.ctx.font = mr.font;
				dc.ctx.fillStyle = mr.color;
				dc.ctx.fillText(mr.text, start, baseline);
				start += mr.x + TIP_SPACING;
			}

		} finally {
			dc.ctx.restore();
		}
	}


	parentToChild(child:Component, x: number, y: number): Coordinates {
		const c = super.parentToChild(child, x, y);
		c.x = c.x + this._scrollX;
		c.y = c.y + this._scrollY;
		return c;
	}

	childToParent(child:Component, x: number, y: number): Coordinates {
		const c = super.childToParent(child, x, y);
		c.x = c.x - this._scrollX;
		c.y = c.y - this._scrollY;
		return c;
	}

	onWheel(e: WheelEvent, localX:number, localY:number, snapWait?:number): boolean {
		let deltaX = e.deltaX;
		let deltaY = e.deltaY;

		if (e.shiftKey) return BUBBLE;		// do standard browser behavior when shift pressed

		if (e.ctrlKey && this.allowHorizontal) {
			/* Switch axes for PC users */
			deltaX = e.deltaY * -1;
			deltaY = e.deltaX;
		}

		if (this.snapScrollTimer) {
			clearTimeout(this.snapScrollTimer);
			this.snapScrollTimer = 0;
		}
		if (this.snapAnimation) {
			this.cancelAnimation(this.snapAnimation);
			this.snapAnimation = 0;
		}
		let wasChange = false;
		if (this.allowHorizontal) {
			const oldScrollX = this.scrollX;
			this.scrollX += deltaX;
			if (oldScrollX !== this.scrollX) {
				wasChange = true;
				this.horizontalPositiveDirection = deltaX > 0;
			}
		}
		if (this.allowVertical) {
			const oldScrollY = this.scrollY;
			this.scrollY += deltaY;
			if (oldScrollY !== this.scrollY) {
				wasChange = true;
				this.verticalPositiveDirection = deltaY > 0;
			}
		}
		if (wasChange) {
			e.preventDefault();	// block the default only if there was a scroll
			const snap = this.children[0].getSnapPosition(this._scrollX, this._scrollY, this.horizontalPositiveDirection, this.verticalPositiveDirection);
			if (snap != null) {
				const startX = this.scrollX;
				const startY = this.scrollY;
				this.snapScrollTimer = setTimeout(() => {
					this.snapAnimation = this.animate({
						animate: (t:number, a: Animation):boolean => {
							if (snap.snapX) this.scrollX = startX + t * (snap.x - startX)
							if (snap.snapY) this.scrollY = startY + t * (snap.y - startY);
							return false;
						},
						finish: (a) => {
						    this.snapAnimation = 0;
						},
						duration: SNAP_DURATION,
						easing: "ease-in"
					});
				}, snapWait ?? SNAP_WAIT);
			}
			this.dirty(false);
		}
		return false;
	}

	scrollTo(x: number, y: number, smooth = false) {
		if (smooth) {
			if (x !== this.scrollX || y !== this.scrollY) {
				const oldScrollX = this.scrollX;
				const oldScrollY = this.scrollY;
				this.animate({
					animate: (t:number, a: Animation):boolean =>  {
						this.scrollX = oldScrollX + t * (x - oldScrollX);
						this.scrollY = oldScrollY + t * (y - oldScrollY);
						return false;
					},
					duration: 500,
					easing: "ease-out"
				});
			}
		} else {
			this.scrollX = x;
			this.scrollY = y;
			this.dirty(false);
		}
	}

	private autoscrollStart:number = 0;
	private lastAutoscroll:number = 0;

	/**
	 * Mark that user is in autoscroll zone and returns speed
	 * @param pos actial position
	 * @param dir positive direction (false = negative direction)
	 **/
	inAutoScrollZone(pos:number, dir:boolean):number {
		const now = Date.now();
		if (this.enteredDragScrollZone) {
			// Not for the first time (already there)
			const timeFromStart = now - this.enteredDragScrollZone;
			if (timeFromStart >= DRAG_SCROLL_WAIT_MS) {
				// Yes we will scroll
				const timeInScrollZone = timeFromStart - DRAG_SCROLL_WAIT_MS;
				let speed;
				if (timeInScrollZone >= TIME_TO_MAX_SPEED) speed = MAX_SPEED;
				else speed = Math.max(MAX_SPEED * (timeInScrollZone / TIME_TO_MAX_SPEED), MIN_SPEED);
				const time = (now - this.lastAutoscroll)/1000;	// time from the last calculation in seconds
				this.lastAutoscroll = now;
				return time * speed * (dir ? 1 : -1);
			} else {
				//this.autoscrollStart = pos;
				this.lastAutoscroll = now;
				return 0;	// Waiting before autoscroll starts
			}
		} else {
			this.enteredDragScrollZone = this.lastAutoscroll = now;
			this.autoscrollStart = pos;
			return 0;
		}
	}

	private autoscrollTimer = 0;
	private autoscrollDirection:AutoscrollDir;

	startAutoscroll(dir:AutoscrollDir) {
		this.autoscrollDirection = dir;
		if (!this.autoscrollTimer) {
			this.autoscrollTimer = setInterval(this.doAutoscroll.bind(this), AUTOSCROLL_INTERVAL);
		}
	}

	stopAutoscroll() {
		if (this.autoscrollTimer) {
			clearInterval(this.autoscrollTimer);
			this.autoscrollTimer = 0;
			this.enteredDragScrollZone = 0;
		}
	}

	doAutoscroll() {
		let deltaX = 0;
		let deltaY = 0;
		switch (this.autoscrollDirection) {
			case "top":
				deltaY = this.inAutoScrollZone(this._scrollY, false);
				break;
			case "bottom":
				deltaY = this.inAutoScrollZone(this._scrollY, true);
				break;
			case "left":
				deltaX = this.inAutoScrollZone(this._scrollX, false);
				break;
			case "right":
				deltaX = this.inAutoScrollZone(this._scrollX, true);
				break;
		}
		if (deltaX !== 0 || deltaY !== 0) this.onWheel(fakeWheel(deltaX, deltaY), 0, 0, 500);
	}

	onDragOver(e: DragEvent, x: number, y: number): boolean {
		let horizontal = this.allowHorizontal ? Math.min(this.horizontalDragScroll, this.width/4) : 0;
		let vertical = this.allowVertical ? Math.min(this.verticalDragScroll, this.height/4) : 0;

		let isInZone = false;

		if (vertical) {
			const top:Rect = { x: 0, y: 0, width: this.width, height: vertical };
			const bottom:Rect = { x: 0, y: this.height-vertical, width: this.width, height: vertical };

			if (isInside(x, y, top)) {
				isInZone = true;
				this.startAutoscroll("top");
			} else if (isInside(x, y, bottom)) {
				isInZone = true;
				this.startAutoscroll("bottom");
			}
		}
		if (horizontal) {
			const left:Rect = { x: 0, y: 0, width: horizontal, height: this.height };
			const right:Rect = { x: this.width-horizontal, y: 0, width: horizontal, height: this.height };

			if (isInside(x, y, left)) {
				isInZone = true;
				this.startAutoscroll("left");
			} else if (isInside(x, y, right)) {
				isInZone = true;
				this.startAutoscroll("right");
			}
		}
		if (!isInZone) this.stopAutoscroll();
		return BUBBLE;
	}



	onTouchStart(e: TouchEvent, x: number, y: number): boolean {
		const ctx = this.root().ctx;
		const inVerticalSB = this.verticalScrollbarShape && ctx.isPointInPath(this.verticalScrollbarShape, x, y);
		const inHorizontalSB = !inVerticalSB && this.horizontalScrollbarShape && ctx.isPointInPath(this.horizontalScrollbarShape, x, y);
		if (inVerticalSB || inHorizontalSB) {
			e.preventDefault();
			this.blockChildren = true;
			this.scrollStartPointerX = x;
			this.scrollStartPointerY = y;
			this.scrollStartX = this._scrollX;
			this.scrollStartY = this._scrollY;
			this.scrollDrag = inVerticalSB ? "vertical" : "horizontal";
			return DONE;
		}
		return BUBBLE;
	}

	onPointerDown(e: PointerEvent, x: number, y: number): boolean {
		const ctx = this.root().ctx;
		const inVerticalSB = this.verticalScrollbarShape && ctx.isPointInPath(this.verticalScrollbarShape, x, y);
		const inHorizontalSB = !inVerticalSB && this.horizontalScrollbarShape && ctx.isPointInPath(this.horizontalScrollbarShape, x, y);
		if (inVerticalSB || inHorizontalSB) {
			e.preventDefault();
			this.setPointerCapture(e);
			this.blockChildren = true;
			this.scrollStartPointerX = x;
			this.scrollStartPointerY = y;
			this.scrollStartX = this._scrollX;
			this.scrollStartY = this._scrollY;
			this.scrollDrag = inVerticalSB ? "vertical" : "horizontal";
			return DONE;
		}
		return BUBBLE;
	}


	onPointerMove(e: PointerEvent, x: number, y: number): boolean {
		if (this.scrollDrag === "no") return BUBBLE;
		else if (this.scrollDrag === "vertical") {
			const pointerOffset = y - this.scrollStartPointerY;
			let newScroll = Math.round(this.scrollStartY + pointerOffset / this.scrollRatio);
			this.scrollY = newScroll;
			this.dirty(false);
		} else {
			const pointerOffset = x - this.scrollStartPointerX;
			let newScroll = Math.round(this.scrollStartX + pointerOffset / this.scrollRatio);
			this.scrollX = newScroll;
			this.dirty(false);
		}
		return DONE;
	}

	onPointerUp(e: PointerEvent, x: number, y: number): boolean {
		this.blockChildren = false;
		this.scrollDrag = "no"
		return BUBBLE;
	}

	findTarget(x: number, y: number): TargetPosition[] {
		const ctx = this.root().ctx;
		const inVerticalSB = this.verticalScrollbarShape && ctx.isPointInPath(this.verticalScrollbarShape, x, y);
		const inHorizontalSB = !inVerticalSB && this.horizontalScrollbarShape && ctx.isPointInPath(this.horizontalScrollbarShape, x, y);
		if (this.blockChildren || inVerticalSB || inHorizontalSB) {
			return [{ target: this, x, y }];
		} else {
			return super.findTarget(x, y);
		}
	}

	onDragLeave(e: DragEvent, x: number, y: number): boolean {
		this.stopAutoscroll();
		return super.onDragLeave(e, x, y);
	}


	onDrop(e: DragEvent, x: number, y: number): boolean {
		this.stopAutoscroll();
		return super.onDrop(e, x, y);
	}


	onDragEnd(e: DragEvent, x: number, y: number): boolean {
		this.stopAutoscroll();
		return super.onDragEnd(e, x, y);
	}
}

function fakeWheel(deltaX: number, deltaY: number): WheelEvent {
	const e:any = {};
	e.deltaX = deltaX;
	e.deltaY = deltaY;
	e.ctrlKey = false;	// no axis switching
	e.shiftKey = false;
	return e as WheelEvent;
}